import Head from "next/head";
import { Box, Typography, Divider, Stack } from "@mui/material";
import LandingLayout from "@/components/layout/LandingLayout";

export default function PrivacyPage() {
  return (
    <>
      <Head>
        <title>Политика конфиденциальности — СпортПодсчет</title>
        <meta
          name="description"
          content="Политика конфиденциальности сервиса СпортПодсчет: какие данные собираем, как используем и как защищаем."
        />
      </Head>

      {/* HEADER */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          mb: { xs: 4, md: 6 },
          bgcolor: "#eaedf1",
        }}
      >
        <Box sx={{ maxWidth: 1000, mx: "auto", px: { xs: 2, md: 3 } }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Политика конфиденциальности
          </Typography>

          <Typography color="text.secondary">
            Здесь описано, какие данные мы собираем, как используем и как защищаем.
          </Typography>
        </Box>
      </Box>

      {/* CONTENT */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ maxWidth: 1000, mx: "auto", px: { xs: 2, md: 3 } }}>
          <Stack spacing={4}>
            <Section title="1. Общие положения">
              <Typography component="p" variant="body2" color="text.secondary" sx={{ m: 0 }}>
                Настоящая Политика конфиденциальности (далее — «Политика») определяет порядок
                обработки и защиты персональных данных пользователей сервиса <b>СпортПодсчет</b>.
              </Typography>
            </Section>

            <Section title="2. Какие данные мы можем собирать">
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  color: "text.secondary",
                  "& li": { mb: 1 },
                }}
              >
                <li>Данные учётной записи: логин, email (если указан).</li>
                <li>Технические данные: IP-адрес, тип устройства/браузера, диагностические события.</li>
                <li>Данные, которые вы вводите в сервис: матчи, команды, составы, настройки табло.</li>
              </Box>

              <Typography component="p" variant="body2" color="text.secondary" sx={{ mt: 2, mb: 0 }}>
                Мы не собираем «чувствительные» персональные данные, если это не требуется по закону
                и не предоставлено вами добровольно.
              </Typography>
            </Section>

            <Section title="3. Цели обработки данных">
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  color: "text.secondary",
                  "& li": { mb: 1 },
                }}
              >
                <li>Предоставление функционала сервиса (хронометраж, табло, история матчей и т.д.).</li>
                <li>Поддержка пользователей и обратная связь.</li>
                <li>Улучшение качества сервиса и диагностика ошибок.</li>
                <li>Обеспечение безопасности (предотвращение злоупотреблений и атак).</li>
              </Box>
            </Section>

            <Section title="4. Правовые основания">
              <Typography component="p" variant="body2" color="text.secondary" sx={{ m: 0 }}>
                Обработка данных осуществляется на основании вашего согласия (в рамках использования
                сервиса), а также в случаях, предусмотренных применимым законодательством.
              </Typography>
            </Section>

            <Section title="5. Передача данных третьим лицам">
              <Typography component="p" variant="body2" color="text.secondary" sx={{ m: 0 }}>
                Мы не продаём и не передаём персональные данные третьим лицам, за исключением
                случаев, когда это необходимо для работы сервиса (например, хостинг/инфраструктура)
                или требуется по закону.
              </Typography>
            </Section>

            <Section title="6. Хранение и защита данных">
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  color: "text.secondary",
                  "& li": { mb: 1 },
                }}
              >
                <li>Мы применяем разумные технические и организационные меры защиты.</li>
                <li>Доступ к данным ограничен и предоставляется по принципу необходимости.</li>
                <li>Срок хранения данных зависит от целей обработки и требований закона.</li>
              </Box>
            </Section>

            <Section title="7. Cookies и локальное хранилище">
              <Typography component="p" variant="body2" color="text.secondary" sx={{ m: 0 }}>
                Сервис может использовать cookies и/или localStorage для авторизации, настроек интерфейса
                и улучшения удобства. Вы можете ограничить использование cookies в настройках браузера,
                но часть функций может работать некорректно.
              </Typography>
            </Section>

            <Section title="8. Права пользователя">
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  color: "text.secondary",
                  "& li": { mb: 1 },
                }}
              >
                <li>Запросить информацию об обработке данных.</li>
                <li>Попросить исправить или удалить данные (если это возможно по закону и техусловиям).</li>
                <li>Отозвать согласие на обработку (в результате доступ к части функций может быть ограничен).</li>
              </Box>
            </Section>

            <Section title="9. Контакты">
              <Typography component="p" variant="body2" color="text.secondary" sx={{ m: 0 }}>
                По вопросам обработки данных: <b>support@sportpodschet.ru</b>
              </Typography>
            </Section>

            <Divider />

            <Typography variant="body2" color="text.secondary">
              Последнее обновление: 05.02.2026
            </Typography>
          </Stack>
        </Box>
      </Box>
    </>
  );
}

PrivacyPage.getLayout = (page) => <LandingLayout>{page}</LandingLayout>;

function Section({ title, children }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={800} gutterBottom>
        {title}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );
}